/**
 * Project: Gus's Auto Dealership - Premium Inventory
 * Author: Eihab Khudhair
 * Description: Enhanced inventory listing with advanced filtering, search, and professional UI
 */

'use client';

import { useEffect, useMemo, useState, useCallback } from 'react';
import { carsApi } from '@/lib/api';
import { CarsResponse, Car } from '@/lib/types';
import Link from 'next/link';
import Image from 'next/image';
import Head from 'next/head';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaFilter, FaSearch, FaCar, FaGasPump,
  FaFire, FaVideo, FaTachometerAlt, FaCog,
  FaTimes, FaStar, FaCheck, FaBolt, FaLeaf, FaPalette,
  FaChevronLeft, FaChevronRight, FaSortAmountDown,
  FaCertificate, FaHeart, FaCheckCircle, FaCopy
} from 'react-icons/fa';
import { GiCarKey } from 'react-icons/gi';

// Type for enhanced filters
type FilterState = {
  make: string;
  model: string;
  minYear: string;
  maxYear: string;
  minPrice: string;
  maxPrice: string;
  fuelType: string;
  transmission: string;
  bodyType: string;
  isHotSale: boolean;
  sortBy: 'price-asc' | 'price-desc' | 'year-desc' | 'mileage-asc' | 'featured';
  page: number;
};

// Quick filter presets
const QUICK_FILTERS = [
  { label: 'Under $20K', minPrice: '0', maxPrice: '20000' },
  { label: '2020+ Models', minYear: '2020', maxYear: '2026' },
  { label: 'Electric/Hybrid', fuelType: 'Electric,Hybrid' },
  { label: 'Low Mileage', sortBy: 'mileage-asc' as const },
  { label: 'Hot Deals', isHotSale: true }
];

export default function PremiumInventoryPage() {
  const [data, setData] = useState<CarsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [favorites, setFavorites] = useState<Set<number>>(new Set());
  const [copiedCarId, setCopiedCarId] = useState<number | null>(null);
  const [copyMessage, setCopyMessage] = useState<string>('');
    const [mounted, setMounted] = useState(false);
  
  const [filters, setFilters] = useState<FilterState>({
    make: '',
    model: '',
    minYear: '',
    maxYear: '',
    minPrice: '',
    maxPrice: '',
    fuelType: '',
    transmission: '',
    bodyType: '',
    isHotSale: false,
    sortBy: 'featured',
    page: 1,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  const siteUrl = 'https://gussauto.com';

  const canonicalUrl = useMemo(() => {
    // Keep canonical clean (page param only). Filters stay indexable for now.
    const url = new URL('/inventory', siteUrl);
    if (filters.page && filters.page > 1) url.searchParams.set('page', String(filters.page));
    return url.toString();
  }, [filters.page]);

  const pageTitle = useMemo(() => {
    const base = "Premium Inventory | Gus's Auto Dealership";
    return filters.page && filters.page > 1 ? `${base} (Page ${filters.page})` : base;
  }, [filters.page]);

  const pageDescription =
    "Browse Gus's Auto Dealership premium used car inventory. Filter by make, model, year, price, fuel type, and more.";

  const fetchCars = useCallback(async () => {
    setLoading(true);
    try {
      type CarsQueryParams = {
        page: number;
        limit: number;
        isAvailable: boolean;
        sortBy: FilterState['sortBy'];
        make?: string;
        model?: string;
        minYear?: string;
        maxYear?: string;
        minPrice?: string;
        maxPrice?: string;
        fuelType?: string;
        transmission?: string;
        bodyType?: string;
        isHotSale?: boolean;
      };

      const params: CarsQueryParams = {
        page: filters.page,
        limit: 12,
        isAvailable: true,
        sortBy: filters.sortBy,
      };

      if (filters.make) params.make = filters.make;
      if (filters.model) params.model = filters.model;
      if (filters.minYear) params.minYear = filters.minYear;
      if (filters.maxYear) params.maxYear = filters.maxYear;
      if (filters.minPrice) params.minPrice = filters.minPrice;
      if (filters.maxPrice) params.maxPrice = filters.maxPrice;
      if (filters.fuelType) params.fuelType = filters.fuelType;
      if (filters.transmission) params.transmission = filters.transmission;
      if (filters.bodyType) params.bodyType = filters.bodyType;
      if (filters.isHotSale) params.isHotSale = true;

      const response = await carsApi.getAll(params);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch cars:', error);
    } finally {
      setLoading(false);
    }
  }, [
    filters.page,
    filters.sortBy,
    filters.make,
    filters.model,
    filters.minYear,
    filters.maxYear,
    filters.minPrice,
    filters.maxPrice,
    filters.fuelType,
    filters.transmission,
    filters.bodyType,
    filters.isHotSale,
  ]);

  // Fetch cars with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCars();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchCars]);

  const handleFilterChange = useCallback((key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  }, []);

  type QuickFilter = { label: string } & Partial<FilterState>;

  const applyQuickFilter = (filter: QuickFilter) => {
    const { ...rest } = filter; // keep all props
    delete (rest as Partial<QuickFilter>).label; // remove UI-only field
    setFilters(prev => ({
      ...prev,
      ...rest,
      page: 1,
    }));
  };

  const clearFilters = () => {
    setFilters({
      make: '',
      model: '',
      minYear: '',
      maxYear: '',
      minPrice: '',
      maxPrice: '',
      fuelType: '',
      transmission: '',
      bodyType: '',
      isHotSale: false,
      sortBy: 'featured',
      page: 1,
    });
  };

  const toggleFavorite = (carId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(carId)) {
        newFavorites.delete(carId);
      } else {
        newFavorites.add(carId);
      }
      return newFavorites;
    });
  };

  const copyCarLink = async (carId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const url = `${window.location.origin}/inventory/${carId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedCarId(carId);
      setCopyMessage('Link copied to clipboard!');
      
      setTimeout(() => {
        setCopiedCarId(null);
        setCopyMessage('');
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getFuelIcon = (type: string) => {
    switch (type) {
      case 'Gasoline': return <FaGasPump className="text-orange-500" />;
      case 'Diesel': return <FaGasPump className="text-gray-700" />;
      case 'Electric': return <FaBolt className="text-blue-500" />;
      case 'Hybrid': return <FaLeaf className="text-green-500" />;
      default: return <FaGasPump />;
    }
  };

  const getCoverMedia = (car: Car) => {
    const media = car?.media || [];
    const firstImage = media.find(m => m?.type !== 'video');
    return firstImage || media[0] || null;
  };

  const cars = useMemo(() => data?.cars || [], [data]);
  
  if (!mounted) {
    return <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-50/95 to-white" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-gray-50/95 to-white">
      {/* SEO: Canonical + Structured Data */}
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />

        {/* OpenGraph helpers (layout already provides defaults, this just strengthens this page) */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${siteUrl}/og.jpg`} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`${siteUrl}/og.jpg`} />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: pageTitle,
              description: pageDescription,
              url: canonicalUrl,
              isPartOf: {
                '@type': 'WebSite',
                name: "Gus's Auto Dealership",
                url: siteUrl,
              },
              mainEntity: {
                '@type': 'ItemList',
                itemListOrder: 'https://schema.org/ItemListOrderAscending',
                numberOfItems: data?.pagination?.total ?? cars.length ?? 0,
                itemListElement: (cars || []).slice(0, 12).map((car, index) => {
                  type CarMedia = { url: string; type?: string };

                  const cover =
                    (car?.media || []).find((m: CarMedia) => m?.type !== 'video') || (car?.media?.[0] as CarMedia | undefined);                  return {
                    '@type': 'ListItem',
                    position: index + 1,
                    url: `${siteUrl}/inventory/${car.id}`,
                    item: {
                      '@type': 'Vehicle',
                      name: `${car.year} ${car.make} ${car.model}`,
                      url: `${siteUrl}/inventory/${car.id}`,
                      image: cover?.url ? [cover.url] : undefined,
                      brand: car.make ? { '@type': 'Brand', name: car.make } : undefined,
                      model: car.model || undefined,
                      vehicleModelDate: car.year || undefined,
                      mileageFromOdometer: car.mileage
                        ? { '@type': 'QuantitativeValue', value: car.mileage, unitCode: 'SMI' }
                        : undefined,
                      fuelType: car.fuelType || undefined,
                      vehicleTransmission: car.transmission || undefined,
                      color: car.color || undefined,
                      offers: {
                        '@type': 'Offer',
                        price: car.price,
                        priceCurrency: 'USD',
                        availability: car.isAvailable
                          ? 'https://schema.org/InStock'
                          : 'https://schema.org/OutOfStock',
                        url: `${siteUrl}/inventory/${car.id}`,
                      },
                    },
                  };
                }),
              },
            }),
          }}
        />
      </Head>
      {/* Enhanced Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white py-20">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&w=1920')] opacity-20 bg-cover bg-center" />
        <div className="container relative mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-3xl mx-auto"
          >
            <GiCarKey className="text-5xl mx-auto mb-6 text-blue-400" />
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-200">
              Premium Automotive Collection
            </h1>
            <p className="text-xl text-blue-200/90 mb-8 leading-relaxed">
              Discover exceptional vehicles curated for the discerning driver. 
              Each car undergoes rigorous inspection and certification.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm flex items-center">
                <FaCheck className="mr-2 text-green-400" /> 200-Point Inspection
              </span>
              <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm flex items-center">
                <FaCertificate className="mr-2 text-yellow-400" /> Certified Pre-Owned
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Enhanced Filter Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl p-8 mb-10 border border-gray-100"
        >
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-6">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Refine Your Selection</h2>
              <p className="text-gray-600">Use our intelligent filters to find your perfect vehicle</p>
            </div>
            
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="px-5 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center shadow-lg hover:shadow-xl"
              >
                <FaFilter className="mr-2" />
                {showAdvancedFilters ? 'Basic Filters' : 'Advanced Filters'}
              </button>
              <button
                onClick={clearFilters}
                className="px-5 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Quick Filters */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <FaBolt className="mr-2 text-yellow-500" />
              Quick Filters
            </h3>
            <div className="flex flex-wrap gap-3">
              {QUICK_FILTERS.map((filter, idx) => (
                <button
                  key={idx}
                  onClick={() => applyQuickFilter(filter)}
                  className="px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg text-gray-700 hover:from-blue-50 hover:to-blue-100 hover:border-blue-300 hover:text-blue-700 transition-all duration-300"
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {/* Main Filters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {/* Make */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-900">
                Vehicle Make
              </label>
              <div className="relative">
                <FaCar className="absolute left-3 top-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by make"
                  value={filters.make}
                  onChange={(e) => handleFilterChange('make', e.target.value)}
                  className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition-all bg-white text-gray-900"
                />
              </div>
            </div>

            {/* Model */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-900">
                Model
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by model"
                  value={filters.model}
                  onChange={(e) => handleFilterChange('model', e.target.value)}
                  className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition-all bg-white text-gray-900"
                />
              </div>
            </div>

            {/* Year Range */}
            <div className="space-y-3">
              <label className="block text-sm font-semibold text-gray-900">
                Year Range
              </label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="number"
                  placeholder="From"
                  value={filters.minYear}
                  onChange={(e) => handleFilterChange('minYear', e.target.value)}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition-all bg-white text-gray-900"
                  min="2000"
                  max="2026"
                />
                <input
                  type="number"
                  placeholder="To"
                  value={filters.maxYear}
                  onChange={(e) => handleFilterChange('maxYear', e.target.value)}
                  className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition-all bg-white text-gray-900"
                  min="2000"
                  max="2026"
                />
              </div>
            </div>

            {/* Sort By */}
            <div className="space-y-3">
              <label htmlFor="sortBy" className="block text-sm font-semibold text-gray-900">
                Sort By
              </label>
              <div className="relative">
                <FaSortAmountDown className="absolute left-3 top-3.5 text-gray-400" />
                <select
                  id="sortBy"
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  className="w-full pl-10 pr-4 py-3.5 border border-gray-300 rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition-all bg-white text-gray-900 appearance-none"
                >
                  <option value="featured">Featured First</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="year-desc">Year: Newest First</option>
                  <option value="mileage-asc">Mileage: Low to High</option>
                </select>
              </div>
            </div>
          </div>

          {/* Advanced Filters */}
          <AnimatePresence>
            {showAdvancedFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-6 border-t border-gray-200">
                  {/* Price Range */}
                  <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-900">
                      Price Range ($)
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <span className="absolute left-3 top-3.5 text-gray-500">$</span>
                        <input
                          type="number"
                          placeholder="Min"
                          value={filters.minPrice}
                          onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                          className="w-full pl-8 pr-4 py-3.5 border border-gray-300 rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition-all bg-white text-gray-900"
                        />
                      </div>
                      <div className="relative">
                        <span className="absolute left-3 top-3.5 text-gray-500">$</span>
                        <input
                          type="number"
                          placeholder="Max"
                          value={filters.maxPrice}
                          onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                          className="w-full pl-8 pr-4 py-3.5 border border-gray-300 rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition-all bg-white text-gray-900"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Fuel Type */}
                  <div className="space-y-3">
                    <label htmlFor="fuelType" className="block text-sm font-semibold text-gray-900">
                      Fuel Type
                    </label>
                    <select
                      id="fuelType"
                      value={filters.fuelType}
                      onChange={(e) => handleFilterChange('fuelType', e.target.value)}
                      className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition-all bg-white text-gray-900"
                    >
                      <option value="">All Fuel Types</option>
                      <option value="Gasoline">⛽ Gasoline</option>
                      <option value="Diesel">⛽ Diesel</option>
                      <option value="Electric">⚡ Electric</option>
                      <option value="Hybrid">🌿 Hybrid</option>
                    </select>
                  </div>

                  {/* Transmission */}
                  <div className="space-y-3">
                    <label htmlFor="transmission" className="block text-sm font-semibold text-gray-900">
                      Transmission
                    </label>
                    <select
                      id="transmission"
                      value={filters.transmission}
                      onChange={(e) => handleFilterChange('transmission', e.target.value)}
                      className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:ring-3 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition-all bg-white text-gray-900"
                    >
                      <option value="">All Transmissions</option>
                      <option value="Automatic">Automatic</option>
                      <option value="Manual">Manual</option>
                      <option value="CVT">CVT</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Active Filters */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-semibold text-gray-900">Active Filters:</span>
              {Object.entries(filters).map(([key, value]) => {
                if (!value || key === 'page' || key === 'sortBy') return null;
                
                return (
                  <div
                    key={key}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-full text-sm text-blue-800"
                  >
                    <span className="font-semibold capitalize">{key}:</span>
                    <span>{value}</span>
                    <button
                      onClick={() => handleFilterChange(key as keyof FilterState, '')}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                      aria-label={`Remove ${key} filter`}
                      title={`Remove ${key} filter`}
                      type="button"
                    >
                      <FaTimes />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Results Section */}
        <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900">
                {data ? (
                  <>
                    <span className="bg-leading-none bg-clip-text text-transparent bg-gradient-to-r from-gray-900 via-emerald-700 to-yellow-600">
                      {data.pagination.total.toLocaleString()}
                    </span>{" "}
                    <span className="text-gray-900">Vehicles</span>
                  </>
                ) : (
                  "Loading..."
                )}
              </h3>

              {data && (
                <span className="px-3 py-1.5 rounded-full text-sm font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                  Available
                </span>
              )}
            </div>

            {(filters.make || filters.model) && (
              <p className="text-gray-600">
                Showing{" "}
                <span className="font-semibold text-gray-900">
                  {filters.make || "All Makes"}
                  {filters.model ? ` • ${filters.model}` : ""}
                </span>
              </p>
            )}

            <div className="h-[2px] w-40 rounded-full bg-gradient-to-r from-emerald-600 via-yellow-500 to-transparent" />
          </div>

          {data && (
            <div className="flex items-center gap-4">
              <span className="text-gray-600">
                Page {filters.page} of {data.pagination.totalPages}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Previous page"
                  title="Previous page"
                  onClick={() => setFilters({ ...filters, page: Math.max(1, filters.page - 1) })}
                  disabled={filters.page === 1}
                  className={`p-2.5 rounded-lg transition-all ${
                    filters.page === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <FaChevronLeft />
                </button>
                <button
                  type="button"
                  aria-label="Next page"
                  title="Next page"
                  onClick={() => setFilters({ ...filters, page: Math.min(data.pagination.totalPages, filters.page + 1) })}
                  disabled={filters.page === data.pagination.totalPages}
                  className={`p-2.5 rounded-lg transition-all ${
                    filters.page === data.pagination.totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Copy Success Message */}
        {copyMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 right-4 z-50 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2"
          >
            <FaCheckCircle />
            <span>{copyMessage}</span>
          </motion.div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: i * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200"
              >
                <div className="h-64 bg-gray-300 animate-pulse" />
                <div className="p-6 space-y-4">
                  <div className="h-6 bg-gray-300 rounded animate-pulse" />
                  <div className="h-4 bg-gray-300 rounded animate-pulse w-2/3" />
                  <div className="grid grid-cols-2 gap-3">
                    {[...Array(4)].map((_, j) => (
                      <div key={j} className="h-3 bg-gray-300 rounded animate-pulse" />
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : cars.length > 0 ? (
          <>
            {/* Enhanced Vehicle Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cars.map((car, index) => {
                const cover = getCoverMedia(car);
                const isVideoCover = cover?.type === 'video';
                const isFavorite = favorites.has(car.id);
                const isCopied = copiedCarId === car.id;
                const carExtra = car as Car & { bodyType?: string; titleStatus?: string };

                return (
                  <motion.div
                    key={car.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    whileHover={{ y: -5 }}
                    className="group"
                  >
                    <Link href={`/inventory/${car.id}`} className="block">
                      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200 hover:border-blue-300 hover:shadow-2xl transition-all duration-300">
                        {/* Media Section */}
                        <div className="relative h-64 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                          {cover ? (
                            isVideoCover ? (
                              <div className="relative w-full h-full">
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-purple-900/20" />
                                <div className="w-full h-full flex items-center justify-center">
                                  <FaVideo className="text-6xl text-white/60" />
                                </div>
                                <div className="absolute bottom-4 right-4 bg-black/80 text-white px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center backdrop-blur-sm">
                                  <FaVideo className="mr-2" /> Video Tour
                                </div>
                              </div>
                            ) : (
                              <Image
                                src={cover.url}
                                alt={`${car.year} ${car.make} ${car.model}`}
                                fill
                                className="object-cover group-hover:scale-110 transition-transform duration-700"
                                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                priority={index < 3}
                              />
                            )
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FaCar className="text-5xl text-gray-400" />
                            </div>
                          )}

                          {/* Overlay Gradient */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                          {/* Action Buttons */}
                          <div className="absolute top-4 right-4 flex flex-col gap-2">
                            <button
                              type="button"
                              aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
                              title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                              onClick={(e) => toggleFavorite(car.id, e)}
                              className={`p-2.5 rounded-full backdrop-blur-sm transition-all ${
                                isFavorite
                                  ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30'
                                  : 'bg-white/20 text-white hover:bg-white/30'
                              }`}
                            >
                              <FaHeart className={isFavorite ? 'fill-current' : ''} />
                            </button>
                            <div className="relative">
                              <button
                                type="button"
                                aria-label="Copy vehicle link"
                                title="Copy vehicle link"
                                onClick={(e) => copyCarLink(car.id, e)}
                                className={`p-2.5 backdrop-blur-sm rounded-full transition-all ${
                                  isCopied
                                    ? 'bg-green-500/20 text-green-500'
                                    : 'bg-white/20 text-white hover:bg-white/30'
                                }`}
                              >
                                {isCopied ? <FaCheckCircle /> : <FaCopy />}
                              </button>
                              {isCopied && (
                                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-black/90 text-white px-2 py-1 rounded text-xs whitespace-nowrap">
                                  Copied!
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Badges */}
                          <div className="absolute top-4 left-4 flex flex-col gap-2">
                            {car.isHotSale && (
                              <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg flex items-center backdrop-blur-sm">
                                <FaFire className="mr-2" /> HOT SALE
                              </div>
                            )}
                            {car.views > 100 && (
                              <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center backdrop-blur-sm">
                                <FaStar className="mr-1.5 text-sm" /> Popular
                              </div>
                            )}
                          </div>

                          {/* Price Tag */}
                          <div className="absolute bottom-4 left-4">
                            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-5 py-3 rounded-xl shadow-2xl">
                              <div className="text-2xl font-bold">${car.price.toLocaleString()}</div>
                            </div>
                          </div>
                        </div>

                        {/* Details Section */}
                        <div className="p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h3 className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                                {car.year} {car.make} {car.model}
                              </h3>
                              {/* Vehicle Identity */}
                              <div className="mt-1 space-y-1">
                                {car.vin && (
                                  <p className="text-sm text-gray-500">
                                    <span className="font-semibold text-gray-600">VIN:</span> {car.vin}
                                  </p>
                                )}

                                <p className="text-sm text-gray-500">
                                  <span className="font-semibold text-gray-600">Body Type:</span>{" "}
                                  {carExtra.bodyType || '—'}
                                </p>

                                <p className="text-sm text-gray-500">
                                  <span className="font-semibold text-gray-600">Title Status:</span>{" "}
                                  {carExtra.titleStatus || '—'}
                                </p>
                              </div>
                            </div>
                            <div className="text-blue-600">
                              <FaCertificate className="text-xl" />
                            </div>
                          </div>

                          {/* Quick Stats */}
                          <div className="grid grid-cols-2 gap-4 mb-6">
                            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                              <div className="p-2 bg-blue-100 rounded-lg">
                                {getFuelIcon(car.fuelType)}
                              </div>
                              <div>
                                <div className="text-sm text-gray-600">Fuel</div>
                                <div className="font-semibold text-gray-900">{car.fuelType}</div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                              <div className="p-2 bg-green-100 rounded-lg">
                                <FaTachometerAlt className="text-green-600" />
                              </div>
                              <div>
                                <div className="text-sm text-gray-600">Mileage</div>
                                <div className="font-semibold text-gray-900">{car.mileage.toLocaleString()} mi</div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                              <div className="p-2 bg-purple-100 rounded-lg">
                                <FaCog className="text-purple-600" />
                              </div>
                              <div>
                                <div className="text-sm text-gray-600">Transmission</div>
                                <div className="font-semibold text-gray-900">{car.transmission}</div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                              <div className="p-2 bg-pink-100 rounded-lg">
                                <FaPalette className="text-pink-600" />
                              </div>
                              <div>
                                <div className="text-sm text-gray-600">Color</div>
                                <div className="font-semibold text-gray-900">{car.color || '—'}</div>
                              </div>
                            </div>
                          </div>

                          {/* Description Preview */}
                          <p className="text-gray-600 mb-6 line-clamp-2">
                            {car.description || 'Immaculate condition with full service history. Ready for immediate delivery.'}
                          </p>

                          {/* CTA Button */}
                          <div className="mt-6">
                            <div className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3.5 rounded-xl font-semibold text-center hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl">
                              View Details
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>

            {/* Enhanced Pagination */}
            {data && data.pagination.totalPages > 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mt-16"
              >
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="text-gray-700">
                      <div className="text-sm font-semibold text-gray-900 mb-1">Showing {((filters.page - 1) * 12) + 1} to {Math.min(filters.page * 12, data.pagination.total)} of {data.pagination.total} vehicles</div>
                      <div className="text-sm">Sorted by: {filters.sortBy.replace('-', ' ')}</div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setFilters({ ...filters, page: Math.max(1, filters.page - 1) })}
                        disabled={filters.page === 1}
                        className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
                          filters.page === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                      >
                        <FaChevronLeft /> Previous
                      </button>

                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, data.pagination.totalPages) }, (_, i) => {
                          let pageNum;
                          if (data.pagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (filters.page <= 3) {
                            pageNum = i + 1;
                          } else if (filters.page >= data.pagination.totalPages - 2) {
                            pageNum = data.pagination.totalPages - 4 + i;
                          } else {
                            pageNum = filters.page - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setFilters({ ...filters, page: pageNum })}
                              className={`w-12 h-12 rounded-xl font-medium transition-all ${
                                pageNum === filters.page
                                  ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg'
                                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => setFilters({ ...filters, page: Math.min(data.pagination.totalPages, filters.page + 1) })}
                        disabled={filters.page === data.pagination.totalPages}
                        className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
                          filters.page === data.pagination.totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                      >
                        Next <FaChevronRight />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20 bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-sm border border-gray-200"
          >
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center">
                <FaCar className="text-4xl text-blue-600" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-3">No Matching Vehicles Found</h3>
              <p className="text-gray-600 mb-8">
                We couldn&apos;t find any vehicles matching your current filters. Try adjusting your search criteria or browse our entire collection.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={clearFilters}
                  className="px-8 py-3.5 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-900 transition-all shadow-lg hover:shadow-xl"
                >
                  Show All Vehicles
                </button>
                <button className="px-8 py-3.5 border-2 border-blue-600 text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-all">
                  Contact Sales
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}